// The desktop thread pane (ADR-003): a resizable side pane that keeps the conversation visible.
// The thread is a conversation of its own — root pinned first, replies in reading order, its own
// composer replying into it.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { data } from '../../bridge/client';
import type { ChannelPage, SendExtras } from '../../bridge/protocol';
import { userDisplayName } from '../../core/channelListDerivation';
import { PostState, type PostRecord } from '../../core/records';
import { Icon } from '../../design/icons';
import { t } from '../../i18n/i18n';
import { useLive, useMinuteClock } from '../hooks';
import { buildConversation, type ConversationModel } from '../models/conversationModel';
import { share } from '../../core/structuralShare';
import { RowKeys } from '../../core/rowIdentity';
import { Composer } from './Composer';
import { useMentionDirectory } from './knownUsernames';
import { rememberEchoFiles } from '../features/attachments/localFiles';
import { useQuickReactions } from './Conversation';
import { useRowActions } from './rowActions';
import type { BodyContext } from './MessageBody';
import { MessageRow, type RowActions } from './MessageRow';
import { useConversationPermissions } from './conversationPermissions';
import { editRights } from '../../core/messageEditRights';
import { useViewingHeartbeat } from '../features/viewing/viewing';
import { ViewingStack } from '../features/viewing/ViewingStack';
import { FollowChip, ThreadTypingLine, useThreadFollow, useThreadSeen, useThreadTyping } from '../features/threads/threadPane';
import { useMentionOpener } from '../features/threads/mentionOpen';
import { useShell } from '../shell/ShellContext';
import { ThreadWindowButton } from '../features/extras/threadWindow';

export function ThreadPane({
  rootId,
  me,
  myUsername,
  onClose,
  onOpenUser,
  onOpenUserId,
  onToast,
  teamName,
  width,
}: {
  rootId: string;
  me: string;
  myUsername: string | null;
  onClose(): void;
  onOpenUser(username: string, anchor?: { x: number; y: number }): void;
  onOpenUserId(userId: string, anchor?: { x: number; y: number }): void;
  onToast(message: string, tone?: 'info' | 'error'): void;
  teamName: string;
  width: number;
}) {
  const [echoes, setEchoes] = useState<PostRecord[]>([]);
  const now = useMinuteClock();
  const quickReactions = useQuickReactions();

  useEffect(() => {
    setEchoes([]);
    void data.call('loadThread', rootId);
  }, [rootId]);

  const { value: page } = useLive<ChannelPage>(() => data.call('threadPage', rootId), ['posts', 'postFiles', 'postReactions', 'users', 'postSeenSummaries', 'customEmoji', 'postAcknowledgements'], [rootId]);
  const current = page && page.posts[0]?.id === rootId ? page : page && page.posts.length === 0 ? null : page;
  // Same sharing and rename rules as the conversation (Conversation.tsx): unchanged rows keep their
  // props' identity, a confirmed reply keeps its row.
  const previousModel = useRef<ConversationModel | null>(null);
  const model = useMemo(() => {
    const built = current ? buildConversation(current, me, null, now, echoes, true) : null;
    const shared = built && previousModel.current ? share(previousModel.current, built) : built;
    previousModel.current = shared;
    return shared;
  }, [current, me, now, echoes]);
  const rowKeys = useMemo(() => new RowKeys(), [rootId]);
  const usersById = model?.usersById;
  const footerNames = useCallback((id: string) => {
    const u = usersById?.get(id);
    return { name: u ? userDisplayName(u) : '?', version: 0 };
  }, [usersById]);

  useEffect(() => {
    if (!current || !echoes.length) return;
    const stored = new Set(current.posts.flatMap((x) => [x.id, x.pendingId ?? '']));
    const left = echoes.filter((e) => !stored.has(e.id));
    if (left.length !== echoes.length) setEchoes(left);
  }, [current, echoes]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const actions: RowActions = useRowActions({ onOpenThread: () => undefined, onOpenUserId, onToast, teamName, quickReactions });
  const { knownUsernames, fullNames: mentionFullNames } = useMentionDirectory(model?.knownUsernames, myUsername);
  // A mention opens the DIRECT conversation; the profile card is the fallback (features/threads).
  const openMention = useMentionOpener(onOpenUser);
  const openUser = useRef(openMention);
  openUser.current = openMention;
  const ctx: BodyContext = useMemo(
    () => ({ knownUsernames, mentionFullNames, currentUsername: myUsername, customEmoji: current?.customEmoji ?? {}, onMention: (u: string, a?: { x: number; y: number }) => openUser.current(u, a) }),
    [knownUsernames, mentionFullNames, myUsername, current?.customEmoji],
  );
  const channelId = current?.channelId ?? '';
  // «Viendo ahora» (shared/API.md §39): the thread on screen is its own scope (root_id = the root).
  useViewingHeartbeat(channelId, rootId);
  const postPermissions = useConversationPermissions(channelId);
  // A subsection of the chat (P056/P073/P143): follow chip, typing slot, own replies' seen checks.
  const shell = useShell();
  const follow = useThreadFollow(rootId, shell.team.id, (m) => onToast(m, 'error'));
  const typingText = useThreadTyping(channelId, rootId, model?.usersById);
  useThreadSeen(channelId, rootId, current?.posts, me);

  const send = (message: string, extras: SendExtras = {}) => {
    if (!channelId) return;
    const pendingId = crypto.randomUUID().replace(/-/g, '');
    const createAt = Date.now();
    setEchoes((e) => [
      ...e,
      { id: pendingId, channelId, userId: me, createAt, updateAt: createAt, deleteAt: 0, message, rootId, pendingId, state: PostState.Pending, type: '', props: null, isPinned: false, editAt: 0, embedJson: null, priority: extras.priority?.priority ?? '' },
    ]);
    rememberEchoFiles(pendingId, channelId, extras.fileIds); // the echo draws its attachments too
    void data.call('send', channelId, message, rootId, pendingId, createAt, extras);
    follow.afterReply(); // the server auto-follows on reply: re-read the chip
  };

  return (
    <aside className="conversation-pane thread-pane" style={{ flex: `0 0 ${width}px`, borderLeft: '1px solid var(--border)' }} aria-label={t('channel.threadTitle')}>
      <div className="wallpaper" />
      <header className="conversation-header">
        <span style={{ color: 'var(--text-muted)', display: 'grid' }}>
          <Icon name="threads" size={18} />
        </span>
        <div className="title-block">
          <h1>{t('channel.threadTitle')}</h1>
        </div>
        {channelId ? <ViewingStack channelId={channelId} rootId={rootId} me={me} /> : null}
        <FollowChip state={follow.state} onToggle={() => void follow.toggle()} />
        <ThreadWindowButton rootId={rootId} onBlocked={(m) => onToast(m, 'error')} />
        <button className="icon-button" onClick={onClose} title={t('common.close')} aria-label={t('common.close')}>
          <Icon name="x" size={17} />
        </button>
      </header>
      <div className="timeline" style={{ flexDirection: 'column' }}>
        <div className="timeline-inner">
          {model
            ? model.rows.map((row) => {
                const author = model.usersById.get(row.post.userId);
                return (
                  <MessageRow
                    key={rowKeys.key(row.post)}
                    row={row}
                    authorName={model.authorName(row.post)}
                    authorVersion={author?.lastPictureUpdate ?? 0}
                    authorIsBot={author?.isBot === true}
                    presence={current?.statuses[row.post.userId] ?? null}
                    isOwn={row.post.userId === me}
                    editRights={editRights(row.post, me, postPermissions)}
                    files={current?.files[row.post.id]}
                    chips={model.chips.get(row.post.id)}
                    footer={undefined}
                    footerNames={footerNames}
                    seen={model.seen.get(row.post.id) ?? null}
                    saved={(current?.saved ?? []).includes(row.post.id)}
                    ctx={ctx}
                    actions={actions}
                    now={now}
                    inThread
                    ack={current?.acks?.[row.post.id]}
                    me={me}
                  />
                );
              })
            : null}
        </div>
      </div>
      <ThreadTypingLine text={typingText} />
      {channelId ? (
        <Composer
          key={rootId}
          channelKey={rootId}
          channelId={channelId}
          rootId={rootId}
          initialDraft={current?.draft ?? ''}
          placeholder={t('channel.threadReplyPlaceholder')}
          participants={model ? [...model.usersById.values()].filter((u) => u.id !== me) : []}
          customEmojiNames={Object.keys(current?.customEmoji ?? {})}
          onSend={send}
          onDraft={(m) => void data.call('saveDraft', channelId, rootId, m)}
          onEdit={actions.onEdit}
          onTyping={() => void data.call('userTyping', channelId, rootId)}
        />
      ) : null}
    </aside>
  );
}
